import type { ProfitLossLine, ProfitLossReport } from '@/services/accounting-service';
import type { TreeReport, TreeReportDefinition, TreeRow, TreeSection } from '@/types/report-definition';
import { groupByGlParentCode } from '@/utils/gl-hierarchy';

export type ProfitLossTreeReportDefinition = TreeReportDefinition<ProfitLossReport>;

const hasUncategorized = (lines: ProfitLossLine[]): boolean => lines.some((l) => !l.glCodeId);

/**
 * Group P&L lines under their dotted-code parent. A code like "7000.06" is
 * treated as a child of parent "7000"; flat codes like "7030" stay solo.
 *
 * Returns rows in the order:
 *   parent heading (no amount)
 *     child 1     amount  ytd
 *     child 2     amount  ytd
 *   Total <parent>  Σ      Σ
 *   <solo row>     amount  ytd
 *
 * Subtotals are emitted as TreeRow with `tone: 'subtotal'` so they bold up in
 * the renderer; the parent heading uses `heading: true` to suppress amounts.
 */
const groupByParent = (
	lines: ProfitLossLine[],
	parentLabels: Record<string, string>,
): TreeRow[] => {
	const out: TreeRow[] = [];
	for (const { parentCode, parent, children } of groupByGlParentCode(lines, (l) => l.code ?? '')) {
		// Solo line (flat code with no children, e.g. 7030 INSURANCE). Just
		// emit the row directly; parent heading would be redundant.
		if (children.length === 0 && parent) {
			out.push(toLeafRow(parent));
			continue;
		}

		// Has children → emit a heading + indented children + subtotal.
		const headingLabel = parentLabels[parentCode]
			?? parent?.label
			?? parentCode;
		out.push({
			id: `head-${parentCode}`,
			code: parentCode,
			label: headingLabel,
			amount: 0,
			heading: true,
		});
		if (parent) {
			// QB labels parent-direct activity as the parent's own name at
			// indent 1; rare but possible if someone posted to the bare parent.
			out.push({ ...toLeafRow(parent), indent: 1 });
		}
		for (const child of children) {
			out.push({ ...toLeafRow(child), indent: 1 });
		}
		const items = parent ? [parent, ...children] : children;
		const subTotal = items.reduce((s, l) => s + l.totalCents, 0);
		const subYtd = items.reduce((s, l) => s + l.ytdCents, 0);
		out.push({
			id: `sub-${parentCode}`,
			label: `Total ${parentCode} · ${headingLabel}`,
			amount: subTotal,
			ytdAmount: subYtd,
			tone: 'subtotal',
		});
	}
	return out;
};

const toLeafRow = (line: ProfitLossLine): TreeRow => ({
	id: line.glCodeId ?? `uncat-${line.code ?? line.label}`,
	glCodeId: line.glCodeId,
	code: line.code,
	label: line.label,
	amount: line.totalCents,
	ytdAmount: line.ytdCents,
	tone: line.glCodeId ? 'normal' : 'uncategorized',
});

const formatPeriodLabel = (startDate: number, endDate: number): string => {
	const start = new Date(startDate);
	const end = new Date(endDate);
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const sm = months[start.getUTCMonth()];
	const em = months[end.getUTCMonth()];
	const sy = String(start.getUTCFullYear()).slice(-2);
	const ey = String(end.getUTCFullYear()).slice(-2);
	if (sy === ey && sm === em) return `${sm} ${sy}`;
	if (sy === ey) return `${sm} - ${em} ${sy}`;
	return `${sm} ${sy} - ${em} ${ey}`;
};

/**
 * Profit & Loss — Income minus Expenses over a date range. Mirrors the print
 * route in `app/print/profit-loss/[id].tsx`. Uncategorized lines (no glCodeId)
 * are flagged so coding gaps stay visible.
 *
 * Layout: QB-style "Ordinary Income/Expense" section with hierarchical
 * dotted-code rollups, "Net Ordinary Income" subtotal, optional "Other
 * Income/Expense" sections when OINC/OEXP/EXEXP activity exists, and a final
 * "Net Income" net-line. Two amount columns — period and fiscal-YTD.
 */
export const profitLossReport: ProfitLossTreeReportDefinition = {
	id: 'profit-loss',
	title: 'Profit & Loss',
	subtitle: 'Income minus expenses for the selected period.',
	flavor: 'tree',
	printUrl: (associationId, params) => {
		const sp = new URLSearchParams();
		if (params.startDate) sp.set('startDate', String(params.startDate));
		if (params.endDate) sp.set('endDate', String(params.endDate));
		if (params.basis) sp.set('basis', String(params.basis));
		const qs = sp.toString();
		return `/print/profit-loss/${associationId}${qs ? `?${qs}` : ''}`;
	},
	buildTree: (data: ProfitLossReport): TreeReport => {
		const uncategorized = data.uncategorized ?? [];
		const showUncatNote = uncategorized.length > 0
			|| hasUncategorized(data.income)
			|| hasUncategorized(data.expenses)
			|| hasUncategorized(data.otherIncome)
			|| hasUncategorized(data.otherExpenses);

		const periodLabel = formatPeriodLabel(data.params.startDate, data.params.endDate);
		const ytdLabel = formatPeriodLabel(data.params.fiscalYearStart, data.params.endDate);

		const incomeRows = groupByParent(data.income, data.parentLabels);
		const expenseRows = groupByParent(data.expenses, data.parentLabels);
		const otherIncomeRows = groupByParent(data.otherIncome, data.parentLabels);
		const otherExpenseRows = groupByParent(data.otherExpenses, data.parentLabels);

		const sections: TreeSection[] = [
			{
				id: 'income',
				title: 'Ordinary Income',
				rows: incomeRows,
				total: {
					label: 'Total Income',
					amount: data.totals.incomeCents,
					ytdAmount: data.totals.ytdIncomeCents,
				},
				emptyMessage: 'No income posted in this period.',
			},
			{
				id: 'expenses',
				title: 'Expense',
				rows: expenseRows,
				total: {
					label: 'Total Expense',
					amount: data.totals.expenseCents,
					ytdAmount: data.totals.ytdExpenseCents,
				},
				emptyMessage: 'No expenses posted in this period.',
			},
			{
				id: 'net-ordinary',
				title: '',
				rows: [
					{
						id: 'net-ordinary-row',
						label: 'Net Ordinary Income',
						amount: data.totals.netOrdinaryCents,
						ytdAmount: data.totals.ytdNetOrdinaryCents,
						tone: 'subtotal',
					},
				],
			},
		];

		const hasOther = otherIncomeRows.length > 0 || otherExpenseRows.length > 0;
		if (hasOther) {
			if (otherIncomeRows.length > 0) {
				sections.push({
					id: 'other-income',
					title: 'Other Income',
					rows: otherIncomeRows,
					total: {
						label: 'Total Other Income',
						amount: data.totals.otherIncomeCents,
						ytdAmount: data.totals.ytdOtherIncomeCents,
					},
				});
			}
			if (otherExpenseRows.length > 0) {
				sections.push({
					id: 'other-expenses',
					title: 'Other Expense',
					rows: otherExpenseRows,
					total: {
						label: 'Total Other Expense',
						amount: data.totals.otherExpenseCents,
						ytdAmount: data.totals.ytdOtherExpenseCents,
					},
				});
			}
			sections.push({
				id: 'net-other',
				title: '',
				rows: [
					{
						id: 'net-other-row',
						label: 'Net Other Income',
						amount: data.totals.otherIncomeCents - data.totals.otherExpenseCents,
						ytdAmount: data.totals.ytdOtherIncomeCents - data.totals.ytdOtherExpenseCents,
						tone: 'subtotal',
					},
				],
			});
		}

		// Uncategorized: GLs with activity that aren't typed income/expense and
		// aren't Balance-Sheet accounts (#184). Shown last, flagged, and folded
		// into Net Income — a prompt to re-type the account, not a silent drop.
		if (uncategorized.length > 0) {
			sections.push({
				id: 'uncategorized',
				title: 'Uncategorized',
				rows: uncategorized.map((line): TreeRow => ({
					id: line.glCodeId ?? `uncat-${line.code ?? line.label}`,
					glCodeId: line.glCodeId,
					code: line.code,
					label: line.code ? `${line.code} · ${line.label}` : line.label,
					amount: line.totalCents,
					ytdAmount: line.ytdCents,
					tone: 'uncategorized',
				})),
				total: {
					label: 'Total Uncategorized',
					amount: data.totals.uncategorizedCents,
					ytdAmount: data.totals.ytdUncategorizedCents,
				},
			});
		}

		return {
			columns: [{ sections }],
			netLine: {
				label: data.totals.netCents < 0 ? 'Net Loss' : 'Net Income',
				amount: data.totals.netCents,
				ytdAmount: data.totals.ytdNetCents,
				isLoss: data.totals.netCents < 0,
			},
			amountColumnLabels: { primary: periodLabel, secondary: ytdLabel },
			note: showUncatNote
				? 'Rows marked "Uncategorized" have a GL account that isn\'t typed as income or expense (or has no GL code). Re-type them in the chart of accounts so they roll into the correct P&L section.'
				: undefined,
		};
	},
};
